import type { CellOrdersState, TeamOrdersState, TurnPhase } from '@badger-sentinel/protocol';

/**
 * Order submission, as the shell sees it (shell.md §Orders).
 *
 * The deadline is global, so *whether you have committed* is shell; *what the
 * order says* is a window's business and lives in `model/builder.ts`.
 */

/** `ORDERS n / t` — Q21: the denominator is orderable friendly assets, not all of them. */
export interface OrdersCount {
  readonly tasked: number;
  readonly orderable: number;
}

export type CellReadiness = 'READY' | 'WORKING' | 'EMPTY';

export interface TeamCellView {
  readonly cell: string;
  readonly readiness: CellReadiness;
  readonly connected: boolean;
  readonly orderCount: number;
}

export function teamReadiness(team: TeamOrdersState | null): readonly TeamCellView[] {
  if (team === null) return [];
  return team.cells.map((cell) => ({
    cell: cell.cell,
    // An unclaimed seat is not "not ready" — nobody is sitting in it, and the
    // team's answer to that is to find a body, not to chase a player.
    readiness: !cell.claimed ? 'EMPTY' : cell.submitted ? 'READY' : 'WORKING',
    connected: cell.connected,
    orderCount: cell.orderCount,
  }));
}

/**
 * The commit control (Q20, shell.md must-not #5).
 *
 * A submitted turn is **not** done: the window is open, the orders can still be
 * refused by the second validation pass (§7.3), and the phase can change under
 * the seat. So the committed state's label names the way *out* — which states
 * the reversibility in the one place a player is looking when they wonder about
 * it — and no state of this button ever says "done", "complete" or "locked in".
 */
export interface CommitView {
  readonly label: string;
  /** The one second line the copy rules allow: a real consequence with a value in it. */
  readonly consequence: string | null;
  readonly submitted: boolean;
  /**
   * Is the orders window open **to this seat**?
   *
   * The builder, the picker keys and the batch tray all read this: it is the
   * answer to "may I compose an order", which is not the same question as "may I
   * mark this cell ready". Missile Warning holding a live track may do the first
   * and not the second.
   */
  readonly enabled: boolean;
  /** May the commit control be pressed? `enabled`, and nothing owing a decision. */
  readonly submitEnabled: boolean;
  /** What the click sends as `submitted`. */
  readonly next: boolean;
  /**
   * Why the commit control is withdrawn, when the reason is one the seat can act
   * on in ten seconds. Null for the ordinary closed-window cases — the countdown
   * beside it already says those.
   */
  readonly withheld: string | null;
}

export function commitFor(args: {
  readonly orders: CellOrdersState | null;
  readonly phase: TurnPhase | null;
  readonly windowOpen: boolean;
  readonly readOnly: boolean;
  /**
   * False until a `CellView` has arrived. The GM seat never gets one — it is
   * served `gm.snapshot` — so without this the umpire's console would offer to
   * submit orders for a cell that does not task.
   */
  readonly capabilitiesKnown: boolean;
  /**
   * **Missile Warning still owes a decision on a live track** (M4-D13).
   *
   * `setSubmitted` throws `MW_TRACKS_OPEN` when `cell === 'MW'` and any track is
   * still open, so without this condition the MW seat is shown an enabled SUBMIT
   * ORDERS whose only possible outcome is an error in the alert lane — Phase 1's
   * finding 11 in a second place, and the REDFOR turn-1 fixture reproduces it.
   * The gate is read from `mwTracks[].open`, which is `isMwTrackOpen` — the same
   * predicate the server's own `MwScheduler.openTracks` filters on, so the two
   * cannot drift.
   *
   * A withdrawal is never gated: the seat may always take a submission back.
   */
  readonly mwTracksOpen: boolean;
}): CommitView {
  const submitted = args.orders?.submitted ?? false;
  const live = args.windowOpen && args.phase === 'ORDERS';
  const blockedByTracks = args.mwTracksOpen && !submitted;
  const enabled = live && !args.readOnly && args.capabilitiesKnown;
  return {
    label: submitted ? 'WITHDRAW ORDERS' : 'SUBMIT ORDERS',
    /*
     * Gated on `enabled`, not on `live`.
     *
     * It used to read `live ? … : null`, which considered neither `readOnly`
     * nor `capabilitiesKnown` — so CREWCOM, whose seat cannot task by rule, and
     * the GM, who is served `gm.snapshot` and never submits anything, were both
     * shown `LOCKS AT T-00:00`. A consequence is a promise about what this
     * click does; a seat that cannot click has no consequence to state.
     */
    consequence: enabled ? 'LOCKS AT T-00:00' : null,
    submitted,
    // §3.3's third invariant: read-only is a server-side flag, and the control
    // stays on screen so the seat can see what it is not permitted to do.
    enabled,
    submitEnabled: enabled && !blockedByTracks,
    next: !submitted,
    // Named, because this one is a thing the operator can fix in ten seconds and
    // the others are not. The rail beside it holds the track it is about.
    withheld: blockedByTracks ? 'OWES A DECISION' : null,
  };
}
